import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { createSocketConnection, authenticateSocket } from '../../../utils/socketConfig';
import { ticketMatchesSearch } from '../utils/viewTicketsUtils';

const API_BASE_URL = import.meta.env.VITE_API_URL;

export const useViewTickets = () => {
  const { user, loading: authLoading, error: authError } = useAuth();
  
  // Main state
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredTickets, setFilteredTickets] = useState([]);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  
  // Notification sound state
  const notificationSoundRef = useRef(new Audio('/Notification.wav'));
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('ictCoorNotificationSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Effect to save sound setting changes
  useEffect(() => {
    localStorage.setItem('ictCoorNotificationSoundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  // Effect to initialize socket connection
  useEffect(() => {
    // Initialize socket connection
    const newSocket = createSocketConnection();

    // Authenticate socket connection
    authenticateSocket(newSocket);

    // Handle authentication confirmation
    newSocket.on('authenticated', (data) => {
      console.log('Socket authentication status:', data);
      
      if (!data.success) {
        console.warn('Socket authentication failed:', data.message);
        setError(`Real-time updates unavailable: ${data.message}`);
      } else {
        console.log(`Socket authenticated as ${data.role}`);
      }
    });

    // Listen for new tickets
    newSocket.on('newTicket', async (ticketData) => {
      console.log('New ticket received:', ticketData);
      
      // Make sure ticket has a details property
      if (!ticketData.details) {
        ticketData.details = {};
      }
      
      // Make sure ticket has a properly formatted requester object
      if (!ticketData.requester) {
        const details = ticketData.categorySpecificDetails?.details || {};
        ticketData.requester = {
          name: ticketData.name || details.requesterName || 'Unknown',
          email: ticketData.email || details.requesterEmail || 'No email provided',
          department: details.department
        };
      }
      
      // Only update if the ticket belongs to the staff's location
      if (user?.locationId && ticketData.locationId === user.locationId) {
        // Update ticket list with the new ticket
        setTickets(prev => [ticketData, ...prev]);
        
        // Show notification
        setNotificationMessage(`New ${ticketData.category} ticket received`);
        setNotificationOpen(true);
        
        // Play notification sound if enabled
        if (soundEnabled) {
          try {
            const sound = notificationSoundRef.current.cloneNode();
            sound.play().catch(err => {
              console.warn('Could not play notification sound:', err.message);
            });
          } catch (error) {
            console.warn('Error playing notification sound:', error);
          }
        }

        // Send email notification
        try {
          // Format the ticket details properly for the email
          const formattedDetails = {
            // Pass all ticket details fields directly 
            ...ticketData.details,
            // Add fields that might be missing but needed for the email template
            typeOfEquipment: ticketData.details?.typeOfEquipment || '',
            modelOfEquipment: ticketData.details?.modelOfEquipment || '',
            serialNo: ticketData.details?.serialNo || '',
            specificProblem: ticketData.details?.specificProblem || ''
          };

          console.log('Sending ticket details to email API:', {
            ticketId: ticketData.id,
            staffEmail: user.email,
            trackingId: ticketData.trackingId,
            category: ticketData.category,
            details: formattedDetails
          });
          
          const response = await fetch(`${API_BASE_URL}/staff/send-ticket-email`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ticketId: ticketData.id,
              staffEmail: user.email,
              ticketDetails: {
                trackingId: ticketData.trackingId,
                category: ticketData.category,
                requester: ticketData.requester?.name || 'Unknown',
                requesterEmail: ticketData.requester?.email || '',
                priority: ticketData.priority,
                status: ticketData.status,
                location: locationInfo?.name,
                details: formattedDetails
              }
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to send email notification');
          }

          console.log('Email notification sent successfully to staff');
        } catch (error) {
          console.error('Error sending email notification:', error);
          // Don't throw the error to prevent the UI from breaking
          // Just log it and continue
        }
      }
    });

    // Listen for ticket updates
    newSocket.on('ticketUpdated', (updatedTicket) => {
      console.log('Ticket update received:', updatedTicket);
      
      // Make sure ticket has a details property
      if (!updatedTicket.details) {
        updatedTicket.details = {};
      }
      
      // Make sure ticket has a properly formatted requester object
      if (!updatedTicket.requester) {
        const details = updatedTicket.categorySpecificDetails?.details || {};
        updatedTicket.requester = {
          name: updatedTicket.name || details.requesterName || 'Unknown',
          email: updatedTicket.email || details.requesterEmail || 'No email provided',
          department: details.department
        };
      }
      
      // Only update if the ticket belongs to the staff's location
      if (user?.locationId && updatedTicket.locationId === user.locationId) {
        // Update the ticket in the list
        setTickets(prev => 
          prev.map(ticket => 
            ticket.id === updatedTicket.id ? updatedTicket : ticket
          )
        );
        
        // Show notification
        setNotificationMessage(`Ticket #${updatedTicket.id} has been updated`);
        setNotificationOpen(true);
        
        // Play notification sound if enabled
        if (soundEnabled) {
          try {
            const sound = notificationSoundRef.current.cloneNode();
            sound.play().catch(err => {
              console.warn('Could not play notification sound:', err.message);
            });
          } catch (error) {
            console.warn('Error playing notification sound:', error);
          }
        }
      }
    });

    // Listen for ticket deletion
    newSocket.on('ticketDeleted', (deletedTicketId) => {
      console.log('Ticket deletion received:', deletedTicketId);
      
      // Remove the deleted ticket from the list
      setTickets(prev => prev.filter(ticket => ticket.id !== deletedTicketId));
      
      // Show notification
      setNotificationMessage(`Ticket #${deletedTicketId} has been deleted`);
      setNotificationOpen(true);
    });

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [user, soundEnabled, locationInfo?.name]);

  useEffect(() => {
    if (!authLoading) {
      if (authError) {
        setError(authError);
        setLoading(false);
      } else if (!user?.locationId) {
        setError('You are not assigned to any location. Please contact your administrator.');
        setLoading(false);
      } else {
        fetchTickets();
      }
    }
  }, [authLoading, user, authError]);

  // Add a new useEffect to retrieve the selected ticket from localStorage on component mount
  useEffect(() => {
    const storedTicket = localStorage.getItem('selectedTicket');
    if (storedTicket) {
      try {
        const parsedTicket = JSON.parse(storedTicket);
        setSelectedTicket(parsedTicket);
        if (localStorage.getItem('viewDetailsOpen') === 'true') {
          setViewDetailsOpen(true);
        }
      } catch (error) {
        console.error('Error parsing stored ticket:', error);
        localStorage.removeItem('selectedTicket');
        localStorage.removeItem('viewDetailsOpen');
      }
    }
  }, []);

  useEffect(() => {
    // Filter tickets based on search query
    const searchStr = searchQuery.toLowerCase();
    const filtered = tickets.filter(ticket => ticketMatchesSearch(ticket, searchStr));
    setFilteredTickets(filtered);
    setPage(0); // Reset to first page when search changes
  }, [searchQuery, tickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/staff/assigned`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch tickets');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch tickets');
      }
      
      // Ensure all tickets have a details property
      const ticketsWithDetails = data.data.map(ticket => {
        if (!ticket.details) {
          ticket.details = {};
        }
        return ticket;
      });
      
      setTickets(ticketsWithDetails);
      setLocationInfo(data.location);

      // Check if there's a stored selectedTicket id and find it in the new ticket list
      const storedTicket = localStorage.getItem('selectedTicket');
      if (storedTicket) {
        try {
          const parsedTicket = JSON.parse(storedTicket);
          const refreshedTicket = ticketsWithDetails.find(ticket => ticket.id === parsedTicket.id);
          if (refreshedTicket) {
            setSelectedTicket(refreshedTicket);
            if (localStorage.getItem('viewDetailsOpen') === 'true') {
              setViewDetailsOpen(true);
            }
          }
        } catch (error) {
          console.error('Error parsing stored ticket:', error);
          localStorage.removeItem('selectedTicket');
          localStorage.removeItem('viewDetailsOpen');
        }
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      if (err.message === 'Failed to fetch') {
        setError('Cannot connect to server. Please check if the server is running.');
      } else {
        setError(err.message || 'Failed to fetch tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter tickets based on search query and filters
  const filterTickets = useCallback(() => {
    return tickets.filter(ticket => {
      const searchMatch = ticketMatchesSearch(ticket, searchQuery.toLowerCase());

      const { startDate, endDate } = dateRange;
      const ticketDate = new Date(ticket.createdAt);
      const dateMatch = (!startDate || ticketDate >= new Date(startDate)) &&
                       (!endDate || ticketDate <= new Date(endDate));
      
      const categoryMatch = selectedCategories.length === 0 || 
                       selectedCategories.includes(ticket.category);
      
      const statusMatch = selectedStatuses.length === 0 || 
                       selectedStatuses.includes(ticket.status);

      return searchMatch && dateMatch && categoryMatch && statusMatch;
    });
  }, [tickets, searchQuery, dateRange, selectedCategories, selectedStatuses]);

  // Update filtered tickets whenever filters change
  useEffect(() => {
    setFilteredTickets(filterTickets());
  }, [filterTickets]);

  return {
    // Main state
    tickets,
    loading,
    error,
    locationInfo,
    user,
    authLoading,
    authError,
    
    // Table state
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    filteredTickets,
    
    // Search and filter state
    searchQuery,
    setSearchQuery,
    selectedCategories,
    selectedStatuses,
    setSelectedStatuses,
    dateRange,
    setDateRange,
    
    // Modal state
    exportModalOpen,
    setExportModalOpen,
    selectedTicket,
    setSelectedTicket,
    viewDetailsOpen,
    setViewDetailsOpen,
    
    // Notification state
    soundEnabled,
    setSoundEnabled,
    notificationOpen,
    setNotificationOpen,
    notificationMessage,
    setNotificationMessage,
    
    // Password state
    passwordDialogOpen,
    setPasswordDialogOpen,
    passwordData,
    setPasswordData,
    passwordError,
    setPasswordError,
    passwordSuccess,
    setPasswordSuccess,
    showPassword,
    setShowPassword,
    
    // Functions
    fetchTickets
  };
};
